import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { fmtINR } from "../../lib/constants";
import { toast } from "sonner";
import DateInput from "../../components/DateInput";
import ImageUploader from "../../components/ImageUploader";
import Paginated from "../../components/Paginated";
import { X, BadgeIndianRupee, Star, Clock, MessageCircle, Hammer } from "lucide-react";

/**
 * Payouts — Super Admin & Major Admin only.
 * Two tabs: Pay Astrologers / Pay Karigars.
 * Left: every person; a dark-maroon "To Pay" list highlights who is owed money.
 * Click a person → their full stats (the same numbers they see in their own
 * dashboard) + the month-end payout form: payment type (UPI / Net Banking /
 * Bank Transfer / NEFT / RTGS / Cash / Cheque), date, reference — cheque needs
 * cheque details, UPI needs the payment screenshot attached. After recording,
 * their outstanding becomes zero in their own dashboard too.
 */
const METHODS = ["UPI", "Net Banking", "Bank Transfer", "NEFT", "RTGS", "Cash", "Cheque"];

const fmtMetal = (g) => {
  const total = Number(g) || 0;
  const grams = Math.floor(total);
  const mg = Math.round((total - grams) * 1000);
  return `${grams}g${mg ? ` ${mg}mg` : ""}`;
};

export default function PayoutsPage() {
  const [tab, setTab] = useState("astrologer");
  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <div className="eyebrow">Month-End Payments by Swayambhu Gold</div>
        <h1 className="font-display text-4xl text-maroon">Payouts</h1>
      </div>
      <div className="flex border border-line overflow-hidden w-fit">
        <button onClick={() => setTab("astrologer")} className={`px-5 py-2.5 text-sm ${tab === "astrologer" ? "bg-maroon text-pearl" : "bg-white"}`} data-testid="tab-pay-astrologer">Pay Astrologers</button>
        <button onClick={() => setTab("karigar")} className={`px-5 py-2.5 text-sm border-l border-line ${tab === "karigar" ? "bg-maroon text-pearl" : "bg-white"}`} data-testid="tab-pay-karigar">Pay Karigars</button>
        <button onClick={() => setTab("pricing")} className={`px-5 py-2.5 text-sm border-l border-line ${tab === "pricing" ? "bg-maroon text-pearl" : "bg-white"}`} data-testid="tab-pay-pricing">Astrology Pricing</button>
      </div>
      {tab === "astrologer" && <AstrologerPayouts/>}
      {tab === "karigar" && <KarigarPayouts/>}
      {tab === "pricing" && <AstrologyPricing/>}
    </div>
  );
}

/* ================= ASTROLOGERS ================= */
function AstrologerPayouts() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);       // selected astrologer summary
  const [detail, setDetail] = useState(null); // full earnings detail
  const [payout, setPayout] = useState(null);

  const load = () => api.get("/api/crm/astrology/earnings").then((r) => setList(r.data?.astrologers || []));
  useEffect(() => { load(); }, []);

  const open = async (a) => {
    setSel(a); setDetail(null);
    const { data } = await api.get(`/api/crm/astrology/earnings?astrologer_id=${a.user_id}`);
    setDetail(data);
  };

  const toPay = list.filter((a) => (a.outstanding || 0) > 0);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Left: all astrologers + dark-maroon To-Pay list */}
      <div className="space-y-4">
        <div className="bg-maroon text-pearl p-4" data-testid="astro-topay">
          <div className="eyebrow text-gold-light mb-2">To Pay ({toPay.length})</div>
          {toPay.length === 0 && <div className="text-xs text-pearl/70">Nothing pending — everyone is settled.</div>}
          {toPay.map((a) => (
            <button key={a.user_id} onClick={() => open(a)}
              className={`w-full text-left flex items-center justify-between py-2 border-b border-pearl/15 ${sel?.user_id === a.user_id ? "text-gold-light" : ""}`}
              data-testid={`topay-astro-${a.user_id}`}>
              <span className="text-sm">{a.name}</span>
              <b className="font-display">{fmtINR(a.outstanding)}</b>
            </button>
          ))}
        </div>
        <div className="bg-white border border-line" data-testid="astro-all">
          <div className="p-3 border-b border-line eyebrow text-[10px]">All Astrologers ({list.length})</div>
          {list.map((a) => (
            <button key={a.user_id} onClick={() => open(a)}
              className={`w-full text-left p-3 border-b border-line/60 hover:bg-pearl ${sel?.user_id === a.user_id ? "bg-pearl" : ""}`}
              data-testid={`astro-${a.user_id}`}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-medium text-sm">{a.name}</div>
                  <div className="text-[10px] text-ash">Commission {a.commission_pct}% · {a.premium_chats || 0} premium chats</div>
                </div>
                <span className={`text-xs font-semibold ${(a.outstanding || 0) > 0 ? "text-maroon" : "text-green-700"}`}>
                  {(a.outstanding || 0) > 0 ? fmtINR(a.outstanding) : "Settled ✓"}
                </span>
              </div>
            </button>
          ))}
          {list.length === 0 && <div className="p-8 text-center text-ash text-sm">No astrologers yet.</div>}
        </div>
      </div>

      {/* Right: detail — replicates the astrologer's own dashboard numbers */}
      <div className="lg:col-span-2">
        {!sel ? (
          <div className="bg-white border border-line p-16 text-center text-ash">Select an astrologer to see their complete details and record a payout.</div>
        ) : !detail ? (
          <div className="bg-white border border-line p-16 text-center text-ash">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white border border-line p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display text-3xl text-maroon">{detail.name}</div>
                  <div className="text-xs text-ash">Swayambhu Gold commission: {detail.commission_pct}%</div>
                </div>
                {(detail.outstanding || 0) > 0 && (
                  <button onClick={() => setPayout({ amount: detail.outstanding, method: "UPI", reference: "", paid_at: "", proof_images: [], note: "" })}
                    className="btn-maroon" data-testid="astro-pay-btn">
                    <BadgeIndianRupee size={14}/> Pay {fmtINR(detail.outstanding)}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4 text-center">
                {[
                  ["Total Chats", detail.chats?.total ?? 0, MessageCircle],
                  ["Premium Chats", detail.premium_chats ?? 0, Star],
                  ["Words Attended", detail.words_attended ?? 0, MessageCircle],
                  ["Avg Turnaround", detail.avg_response_minutes != null ? `${detail.avg_response_minutes} min` : "—", Clock],
                  ["Rating", detail.avg_rating != null ? `${detail.avg_rating} ★ (${detail.ratings_count})` : "—", Star],
                  ["Gross Collected", fmtINR(detail.gross_collected || 0), BadgeIndianRupee],
                  ["Their Share (LTV)", fmtINR(detail.revenue?.total || 0), BadgeIndianRupee],
                  ["Paid Till Now", fmtINR(detail.total_paid_out || 0), BadgeIndianRupee],
                ].map(([l, v, Icon]) => (
                  <div key={l} className="border border-line p-3">
                    <Icon size={13} className="mx-auto text-gold mb-1"/>
                    <div className="font-display text-lg text-maroon">{v}</div>
                    <div className="text-[8px] uppercase tracking-widest text-ash">{l}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 mt-3 text-center">
                {[["Today", detail.revenue?.day], ["Week", detail.revenue?.week], ["Month", detail.revenue?.month], ["6 Months", detail.revenue?.six_months], ["Year", detail.revenue?.year], ["Lifetime", detail.revenue?.total]].map(([l, v]) => (
                  <div key={l} className="bg-pearl border border-line p-2">
                    <div className="font-display text-sm text-maroon">{fmtINR(v || 0)}</div>
                    <div className="text-[8px] uppercase tracking-widest text-ash">{l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-sm">
                Outstanding to pay: <b className="text-maroon font-display text-xl">{fmtINR(detail.outstanding || 0)}</b>
                <span className="text-xs text-ash ml-2">(becomes ₹0 in their dashboard once you record the payout)</span>
              </div>
            </div>

            {/* Payout logs */}
            <div className="bg-white border border-line">
              <div className="p-3 border-b border-line eyebrow text-[10px]">Payout Logs</div>
              <Paginated items={detail.payout_logs || []} pageSize={6}>
                {(pageItems) => pageItems.map((p) => (
                  <div key={p.id} className="p-3 border-b border-line/60 text-xs flex flex-wrap justify-between gap-2" data-testid={`astro-payout-${p.id}`}>
                    <span>{new Date(p.paid_at).toLocaleDateString("en-GB")} · {p.method}{p.reference ? ` · ${p.reference}` : ""} · by {p.paid_by}</span>
                    <span className="flex items-center gap-2">
                      {(p.proof_images || []).map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" className="h-6 w-6 object-cover border border-line"/></a>)}
                      <b className="text-maroon">{fmtINR(p.amount)}</b>
                    </span>
                  </div>
                ))}
              </Paginated>
              {(detail.payout_logs || []).length === 0 && <div className="p-6 text-center text-ash text-xs">No payouts recorded yet.</div>}
            </div>
          </div>
        )}
      </div>

      {payout && sel && (
        <PayoutModal
          title={`Pay ${sel.name} (Astrologer)`}
          state={payout} setState={setPayout}
          onSubmit={async () => {
            await api.post("/api/crm/astrology/payouts", { ...payout, astrologer_id: sel.user_id, paid_at: payout.paid_at || undefined });
            toast.success("Payout recorded — astrologer's outstanding is now zero");
            setPayout(null); load(); open(sel);
          }}
        />
      )}
    </div>
  );
}

/* ================= KARIGARS ================= */
function KarigarPayouts() {
  const [karigars, setKarigars] = useState([]);
  const [earnMap, setEarnMap] = useState({});   // id → earnings
  const [sel, setSel] = useState(null);
  const [payout, setPayout] = useState(null);

  const load = async () => {
    const { data } = await api.get("/api/crm/users?role=karigar");
    const list = data || [];
    setKarigars(list);
    const map = {};
    await Promise.all(list.map(async (k) => {
      try { const { data: e } = await api.get(`/api/crm/karigar/earnings?karigar_id=${k.id}`); map[k.id] = e; } catch {}
    }));
    setEarnMap(map);
  };
  useEffect(() => { load(); }, []);

  const toPay = karigars.filter((k) => (earnMap[k.id]?.outstanding || 0) > 0);
  const d = sel ? earnMap[sel.id] : null;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="space-y-4">
        <div className="bg-maroon text-pearl p-4" data-testid="karigar-topay">
          <div className="eyebrow text-gold-light mb-2">To Pay ({toPay.length})</div>
          {toPay.length === 0 && <div className="text-xs text-pearl/70">Nothing pending — everyone is settled.</div>}
          {toPay.map((k) => (
            <button key={k.id} onClick={() => setSel(k)}
              className={`w-full text-left flex items-center justify-between py-2 border-b border-pearl/15 ${sel?.id === k.id ? "text-gold-light" : ""}`}
              data-testid={`topay-karigar-${k.id}`}>
              <span className="text-sm">{k.name}</span>
              <b className="font-display">{fmtINR(earnMap[k.id]?.outstanding || 0)}</b>
            </button>
          ))}
        </div>
        <div className="bg-white border border-line" data-testid="karigar-all">
          <div className="p-3 border-b border-line eyebrow text-[10px]">All Karigars ({karigars.length})</div>
          {karigars.map((k) => (
            <button key={k.id} onClick={() => setSel(k)}
              className={`w-full text-left p-3 border-b border-line/60 hover:bg-pearl ${sel?.id === k.id ? "bg-pearl" : ""}`}
              data-testid={`karigar-${k.id}`}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-medium text-sm">{k.name}</div>
                  <div className="text-[10px] text-ash">{k.specialization || "Karigar"}</div>
                </div>
                <span className={`text-xs font-semibold ${(earnMap[k.id]?.outstanding || 0) > 0 ? "text-maroon" : "text-green-700"}`}>
                  {(earnMap[k.id]?.outstanding || 0) > 0 ? fmtINR(earnMap[k.id].outstanding) : "Settled ✓"}
                </span>
              </div>
            </button>
          ))}
          {karigars.length === 0 && <div className="p-8 text-center text-ash text-sm">No karigars yet.</div>}
        </div>
      </div>

      <div className="lg:col-span-2">
        {!sel ? (
          <div className="bg-white border border-line p-16 text-center text-ash">Select a karigar to see their complete details and record a payout.</div>
        ) : !d ? (
          <div className="bg-white border border-line p-16 text-center text-ash">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white border border-line p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display text-3xl text-maroon">{sel.name}</div>
                  <div className="text-xs text-ash">{sel.specialization || "Karigar"}</div>
                </div>
                {(d.outstanding || 0) > 0 && (
                  <button onClick={() => setPayout({ amount: d.outstanding, method: "UPI", reference: "", paid_at: "", proof_images: [], note: "" })}
                    className="btn-maroon" data-testid="karigar-pay-btn">
                    <BadgeIndianRupee size={14}/> Pay {fmtINR(d.outstanding)}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4 text-center">
                {[
                  ["Jobs Delivered", (d.jobs || []).length, Hammer],
                  ["Total Earned (LTV)", fmtINR(d.total_earned || 0), BadgeIndianRupee],
                  ["Paid Till Now", fmtINR(d.total_paid || 0), BadgeIndianRupee],
                  ["Outstanding", fmtINR(d.outstanding || 0), BadgeIndianRupee],
                ].map(([l, v, Icon]) => (
                  <div key={l} className="border border-line p-3">
                    <Icon size={13} className="mx-auto text-gold mb-1"/>
                    <div className="font-display text-lg text-maroon">{v}</div>
                    <div className="text-[8px] uppercase tracking-widest text-ash">{l}</div>
                  </div>
                ))}
              </div>
              {/* Metal given instead of money */}
              <div className="grid grid-cols-2 gap-3 mt-3 text-center max-w-md">
                <div className="border-2 border-gold p-3">
                  <div className="font-display text-lg text-maroon">{fmtMetal(d.gold_received_g)}</div>
                  <div className="text-[8px] uppercase tracking-widest text-ash">Gold given instead of money</div>
                </div>
                <div className="border-2 border-gold p-3">
                  <div className="font-display text-lg text-maroon">{fmtMetal(d.silver_received_g)}</div>
                  <div className="text-[8px] uppercase tracking-widest text-ash">Silver given instead of money</div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-line">
              <div className="p-3 border-b border-line eyebrow text-[10px]">Payout Logs</div>
              <Paginated items={d.payouts || []} pageSize={6}>
                {(pageItems) => pageItems.map((p) => (
                  <div key={p.id} className="p-3 border-b border-line/60 text-xs flex flex-wrap justify-between gap-2" data-testid={`karigar-payout-${p.id}`}>
                    <span>{new Date(p.paid_at).toLocaleDateString("en-GB")} · {p.method}{p.reference ? ` · ${p.reference}` : ""} · by {p.paid_by}</span>
                    <span className="flex items-center gap-2">
                      {(p.proof_images || []).map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" className="h-6 w-6 object-cover border border-line"/></a>)}
                      <b className="text-maroon">{fmtINR(p.amount)}</b>
                    </span>
                  </div>
                ))}
              </Paginated>
              {(d.payouts || []).length === 0 && <div className="p-6 text-center text-ash text-xs">No payouts recorded yet.</div>}
            </div>
          </div>
        )}
      </div>

      {payout && sel && (
        <PayoutModal
          title={`Pay ${sel.name} (Karigar)`}
          state={payout} setState={setPayout}
          onSubmit={async () => {
            await api.post("/api/crm/karigar/payouts", { ...payout, karigar_id: sel.id, paid_at: payout.paid_at || undefined });
            toast.success("Payout recorded — karigar's outstanding is now zero");
            setPayout(null); load();
          }}
        />
      )}
    </div>
  );
}

/* ================= SHARED PAYOUT MODAL ================= */
function PayoutModal({ title, state, setState, onSubmit }) {
  const [busy, setBusy] = useState(false);
  const needsProof = state.method === "UPI" || state.method === "Cheque";
  const submit = async (e) => {
    e.preventDefault();
    if (state.method === "Cheque" && !state.reference.trim()) { toast.error("Enter the cheque details (number, bank, date on cheque)"); return; }
    if (needsProof && (state.proof_images || []).length === 0) { toast.error(state.method === "UPI" ? "Attach the UPI payment screenshot" : "Attach the cheque image"); return; }
    setBusy(true);
    try { await onSubmit(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setState(null)}>
      <form onSubmit={submit} className="bg-white border border-line p-6 w-full max-w-md space-y-3 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl text-maroon">{title}</div>
          <button type="button" onClick={() => setState(null)}><X size={18}/></button>
        </div>
        <label className="block text-sm"><span className="eyebrow text-[10px]">Amount (₹)</span>
          <input type="number" required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={state.amount} onChange={(e) => setState({...state, amount: +e.target.value})} data-testid="payout-amount"/>
        </label>
        <label className="block text-sm"><span className="eyebrow text-[10px]">By which means is the payment being made?</span>
          <select className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={state.method} onChange={(e) => setState({...state, method: e.target.value})} data-testid="payout-method">
            {METHODS.map((m) => <option key={m}>{m}</option>)}
          </select>
        </label>
        <label className="block text-sm">
          <span className="eyebrow text-[10px]">
            {state.method === "Cheque" ? "Cheque details (number, bank, date on cheque)" : state.method === "Cash" ? "Note (optional)" : "Reference / transaction ID"}
          </span>
          <input className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={state.reference} onChange={(e) => setState({...state, reference: e.target.value})} data-testid="payout-reference"/>
        </label>
        <label className="block text-sm"><span className="eyebrow text-[10px]">Payment date</span>
          <DateInput className="mt-1" yearsBack={1} yearsAhead={0} value={state.paid_at ? state.paid_at.slice(0, 10) : ""} onChange={(v) => setState({...state, paid_at: v})} data-testid="payout-date"/>
        </label>
        <div>
          <div className="eyebrow text-[10px] mb-1">
            Proof {state.method === "UPI" ? "(UPI payment screenshot — required)" : state.method === "Cheque" ? "(cheque photo — required)" : "(optional)"}
          </div>
          <ImageUploader value={state.proof_images} onChange={(urls) => setState({...state, proof_images: urls})} kind="payout" maxFiles={3}/>
        </div>
        <button disabled={busy} className="btn-maroon w-full justify-center" data-testid="payout-submit">{busy ? "Recording…" : "Record Payout"}</button>
      </form>
    </div>
  );
}


/* ================= ASTROLOGY PRICING (Super / Major Admin only) ================= */
function AstrologyPricing() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/api/crm/pricing/astrology").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  if (!form) return <div className="bg-white border border-line p-6 text-sm text-ash">Loading…</div>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/crm/pricing/astrology", form);
      toast.success("Astrology pricing updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const Field = ({ k, label, hint, step }) => (
    <label className="text-sm">
      <span className="eyebrow text-[10px]">{label}</span>
      <input
        type="number" step={step || 1}
        className="w-full border border-line px-3 py-2 bg-pearl mt-1"
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: +e.target.value })}
        data-testid={`pricing-${k}`}
      />
      {hint && <span className="text-[10px] text-ash block mt-1">{hint}</span>}
    </label>
  );

  return (
    <form onSubmit={save} className="bg-white border border-line p-6 space-y-5 max-w-2xl" data-testid="astrology-pricing">
      <div>
        <h2 className="font-display text-2xl text-maroon">Astrology Chat Pricing</h2>
        <p className="text-[11px] text-ash mt-1">
          A customer chats free up to the trial limit (words <b>or</b> characters, whichever runs out first).
          After that each payment buys one block of words and the counter restarts at zero — so once a
          block is used up, the next word needs the next payment.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field k="astrology_premium_price" label="Price per paid block (₹)" step="0.01"/>
        <Field k="astrology_photo_price" label="Charge to attach a photo (₹)" step="0.01"/>
        <Field k="astrology_free_words" label="Free trial — words"/>
        <Field k="astrology_free_chars" label="Free trial — characters"/>
        <Field k="astrology_premium_words" label="Words per paid block"/>
      </div>

      <div className="bg-pearl border border-line p-3 text-[11px] text-ash">
        With the current values: the first <b>{form.astrology_free_words} words</b> (or{" "}
        <b>{form.astrology_free_chars} characters</b>) are free. Then ₹{form.astrology_premium_price} buys{" "}
        <b>{form.astrology_premium_words} words</b>; word {form.astrology_premium_words + 1} needs another
        ₹{form.astrology_premium_price}. Attaching a photo costs ₹{form.astrology_photo_price}.
      </div>

      <button className="btn-maroon" disabled={saving} data-testid="pricing-save">
        {saving ? "Saving…" : "Save Pricing"}
      </button>
    </form>
  );
}
